import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from 'lit';
import { property, query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import type { SelectTag } from '../../logical/index.js';
import { getColorByColor } from './colors.js';

export class MultiTagView extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    affine-multi-tag-view {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      min-height: 22px;
    }

    .affine-select-cell-container * {
      box-sizing: border-box;
    }

    .affine-select-cell-container {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      width: 100%;
      font-size: var(--streamlain-font-sm);
    }

    /* Pill is a visual atom — stays a single-line rounded rectangle
       regardless of the cell's expand state. Inheriting the cell-
       level dv-cell vars broke it in expanded mode: white-space
       normal wrapped text across lines, overflow visible let the
       wrapped content spill outside the pill background. Hardcode
       the pill geometry instead: max-width 100% + ellipsis for
       tight columns, flex-wrap on the container for multi-line
       layout in expanded mode. */
    .affine-select-cell-container .select-selected {
      height: 22px;
      max-width: 100%;
      box-sizing: border-box;
      font-size: 14px;
      line-height: 20px;
      padding: 0 8px;
      border-radius: 4px;
      white-space: nowrap;
      background: var(--streamlain-tag-white);
      overflow: hidden;
      text-overflow: ellipsis;
      border: 1px solid ${unsafeCSSVarV2('database/border')};
      /* Raised chip palette (chip/label/{blue,green,…}) is now
         saturated enough that only white text reads cleanly on
         top. Chained class selector bumps specificity to (0,2,0)
         to beat the fullscreen viewport's descendant
         \`color: #141414\` rule (spec 0,1,1). */
      color: #ffffff;
    }
    .affine-select-cell-container .select-selected.select-selected {
      color: #ffffff;
    }
  `;

  override render() {
    const values = this.value;
    const map = new Map<string, SelectTag>(this.options?.map(v => [v.id, v]));
    return html`
      <div contenteditable="false" class="affine-select-cell-container">
        ${repeat(values, id => {
          const option = map.get(id);
          if (!option) {
            return;
          }
          const style = styleMap({
            backgroundColor: getColorByColor(option.color),
          });
          return html`<span
            data-testid="tag-selected"
            class="select-selected"
            style=${style}
            >${option.value}</span
          >`;
        })}
      </div>
    `;
  }

  @property({ attribute: false })
  accessor options: SelectTag[] = [];

  @query('.affine-select-cell-container')
  accessor selectContainer!: HTMLElement;

  @property({ attribute: false })
  accessor value: string[] = [];
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-multi-tag-view': MultiTagView;
  }
}
